import type { Product } from '@/types'

// Strain classification → Tailwind color tokens. Same four groups the
// filter modal uses (Indica/Sativa/Hybrid/Blend); dominant variants
// inherit the parent's color so "Sativa Dominant" reads the same as
// "Sativa" at a glance. Anything that doesn't match one of the four
// keyword groups falls through to a neutral gray (e.g. "CBD" or other
// specialty types).
export type StrainColorGroup = 'Indica' | 'Sativa' | 'Hybrid' | 'Blend' | null

export function classifyTypeName(name: string | null | undefined): StrainColorGroup {
  if (!name) return null
  const n = name.trim()
  if (!n) return null
  if (n.toLowerCase() === 'hybrid') return 'Hybrid'
  if (/indica/i.test(n)) return 'Indica'
  if (/sativa/i.test(n)) return 'Sativa'
  if (/blend/i.test(n)) return 'Blend'
  return null
}

// Badge classes for the colored chip rendered on product cards. Light
// background + same-hue text matches the existing strain badge pattern
// (gray fallback when the type doesn't classify).
export function typeBadgeClasses(name: string | null | undefined): string {
  switch (classifyTypeName(name)) {
    case 'Indica':
      return 'bg-blue-100 text-blue-700'
    case 'Sativa':
      return 'bg-orange-100 text-orange-700'
    case 'Hybrid':
      return 'bg-purple-100 text-purple-700'
    case 'Blend':
      return 'bg-amber-100 text-amber-700'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

// Same colors as a value-text class for the modal's Tile component
// (which renders label + value text on a gray tile background).
export function typeValueTextClass(name: string | null | undefined): string {
  switch (classifyTypeName(name)) {
    case 'Indica':
      return 'text-blue-600'
    case 'Sativa':
      return 'text-orange-600'
    case 'Hybrid':
      return 'text-purple-600'
    case 'Blend':
      return 'text-amber-600'
    default:
      return ''
  }
}

// Pull the type name off a product, tolerating both shapes the BE may
// return (nested object on /products/search, bare string on legacy
// endpoints). Empty string when absent.
//
// "Dominant" is stripped from the display name ("Indica Dominant" →
// "Indica") because the badge real-estate is tight and the cashier
// already knows from the color (and grouping in the filter modal)
// that dominant variants roll up under their parent. Filter / chip
// classification still uses the raw name via classifyTypeName, so
// this purely affects what the card and modal render.
export function getProductTypeName(product: Product): string {
  if (product.product_type && typeof product.product_type === 'object') {
    const raw = product.product_type.name?.trim() ?? ''
    return raw.replace(/\s+dominant\b/i, '').trim()
  }
  return ''
}
